import { differenceInCalendarDays, isSameDay, startOfDay, startOfWeek } from "date-fns";
import { pl } from "date-fns/locale";
import type { Completion } from "@/lib/schemas";

/**
 * Streak = liczba kolejnych dni z >=1 zatwierdzonym wykonaniem,
 * licząc wstecz od dziś (lub od wczoraj, jeśli dziś jeszcze brak).
 */
export function calculateStreak(approved: Completion[]): number {
  return calculateStreakWithShield(approved, [], false).streak;
}

export type StreakResult = {
  streak: number;
  /** Czy tarcza była dostępna w momencie liczenia. */
  shieldAvailable: boolean;
  /**
   * Dni które zostały uratowane DURING tej kalkulacji (tarcza zużyta).
   * Caller powinien zapisać je do member.streakShieldRescues żeby
   * następne renderowanie traktowało je jako już zatwierdzone.
   */
  newRescues: Date[];
};

/**
 * Wariant `calculateStreak` z mechanizmem „tarczy" — pozwala uratować
 * jeden zerwany dzień raz na tydzień.
 *
 * Reguły:
 * - Idziemy wstecz od dziś (lub wczoraj, jeśli dziś jeszcze brak).
 * - Dzień liczy się jako wykonany jeśli jest w `approved` LUB w `rescuedDates`.
 * - Gdy trafimy na pierwszy dzień bez wykonania:
 *     - Jeśli `shieldAvailable` ORAZ luka jest w bieżącym tygodniu →
 *       uratuj go (count++, dorzuć do newRescues), wyłącz dalszą
 *       dostępność tarczy w tej kalkulacji.
 *     - W przeciwnym razie → koniec passy.
 *
 * **Tarcza działa tylko dla luki w bieżącym tygodniu kalendarzowym.**
 * Inaczej (bug 2026-05-11): walk wstecz znajduje gap głęboko w przeszłości,
 * shieldAvailable=true bo żaden rescue nie jest z tego tygodnia, więc
 * tarcza ratuje stary dzień. Następny tydzień: nowy rescue, kolejny stary
 * dzień ratowany. Streak rośnie indefinitely wstecz.
 */
export function calculateStreakWithShield(
  approved: Completion[],
  rescuedDates: Date[],
  shieldAvailable: boolean,
): StreakResult {
  const days = new Set<number>();
  for (const c of approved) {
    days.add(startOfDay(c.dueDate.toDate()).getTime());
  }
  for (const d of rescuedDates) {
    days.add(startOfDay(d).getTime());
  }

  const today = startOfDay(new Date()).getTime();
  const yesterday = today - 24 * 3600 * 1000;
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1, locale: pl }).getTime();

  let cursor = days.has(today) ? today : days.has(yesterday) ? yesterday : null;
  if (cursor === null) {
    // Można uratować dzisiaj/wczoraj? Tak: jeśli ani dziś ani wczoraj
    // nie ma wykonania, ale tarcza dostępna i istnieją wcześniejsze dni
    // pod rząd — uratuj WCZORAJ (nie dziś, bo dzień jeszcze trwa).
    if (!shieldAvailable) return { streak: 0, shieldAvailable, newRescues: [] };
    // Wczoraj musi być w tym tygodniu — inaczej luka jest za stara.
    if (yesterday < weekStart) {
      return { streak: 0, shieldAvailable, newRescues: [] };
    }
    const yDate = new Date(yesterday);
    const dayBefore = yesterday - 24 * 3600 * 1000;
    if (!days.has(dayBefore)) {
      // jeden dzień nie wystarczy żeby uratować passę dłuższą niż 0
      return { streak: 0, shieldAvailable, newRescues: [] };
    }
    // ratujemy wczoraj i kontynuujemy passę od dnia poprzedzającego
    let count = 1; // wczoraj jako uratowane
    let c = dayBefore;
    while (days.has(c)) {
      count += 1;
      c -= 24 * 3600 * 1000;
    }
    return {
      streak: count,
      shieldAvailable,
      newRescues: [yDate],
    };
  }

  let count = 0;
  let canRescue = shieldAvailable;
  const newRescues: Date[] = [];
  while (true) {
    if (days.has(cursor)) {
      count += 1;
      cursor -= 24 * 3600 * 1000;
      continue;
    }
    // Tarcza ratuje tylko gap w bieżącym tygodniu kalendarzowym.
    if (canRescue && cursor >= weekStart) {
      newRescues.push(new Date(cursor));
      count += 1;
      canRescue = false;
      cursor -= 24 * 3600 * 1000;
      continue;
    }
    break;
  }
  return { streak: count, shieldAvailable, newRescues };
}

export type DailyEarnings = { date: Date; label: string; amountMinor: number };

/**
 * Zarobki per dzień w ostatnich N dniach (z dziś).
 */
export function earningsByDay(
  approved: Completion[],
  days: number,
): DailyEarnings[] {
  const today = startOfDay(new Date());
  const buckets: DailyEarnings[] = [];
  const dayLabels = ["Nd", "Pn", "Wt", "Śr", "Cz", "Pt", "Sb"];

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    buckets.push({ date: d, label: dayLabels[d.getDay()], amountMinor: 0 });
  }

  for (const c of approved) {
    const ts = startOfDay(c.dueDate.toDate());
    const diff = differenceInCalendarDays(today, ts);
    if (diff < 0 || diff >= days) continue;
    const idx = days - 1 - diff;
    buckets[idx].amountMinor += c.rewardMinor;
  }

  return buckets;
}

export { isSameDay };

/**
 * Liczy ile completions zostało zatwierdzonych w ostatnich N dniach (włącznie z dziś).
 */
export function countCompletionsInLastDays(
  approved: Completion[],
  days: number,
): number {
  const cutoff = Date.now() - days * 24 * 3600 * 1000;
  return approved.filter((c) => c.dueDate.toDate().getTime() >= cutoff).length;
}
